/*
 * OrbitDeploy — Stage 2 outcome `direct_deploy`.
 *
 * Triggered after a user picks the Marketplace card in Stage 1 and
 * Stage 2 confirms bypass eligible (clarity ≥ 0.525). The matched
 * marketplace solution (today: AI Chatbot; tomorrow: other SaaS) is
 * ready to ship — no PRD, no validx build, just configure deploy
 * channels and go live.
 *
 * Flow:
 *   1. Fetch engagement via /api/intake/engagement/:eid
 *   2. Render matched solution + tier badge + channel pickers
 *   3. CTA "Deploy now" → routes to the solution's detail page
 *      (today /marketplace/chatbot; the detail page handles the
 *      actual channel integration UX).
 *
 * Cost-shape note: this outcome does NOT burn an agent_request quota.
 * Marketplace solutions are pre-built and included on every tier with
 * a captured website (Free gets 1 chatbot deploy lifetime).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag, ChevronLeft, ChevronRight, Globe, MessageSquare,
  Sparkles, Loader2,
} from 'lucide-react';


const API_BASE = process.env.REACT_APP_API_URL ?? '';


interface EngagementDetail {
  engagement_id: string;
  status: string;
  tier_at_decision: string | null;
  stage1_pick: string | null;
  stage2_decision: any;
  stage1_solution: any;
  l1_summary: any;
}


export default function OrbitDeploy() {
  const navigate = useNavigate();
  const { engagementId } = useParams<{ engagementId: string }>();
  const [data, setData] = useState<EngagementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      if (!engagementId) return;
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE}/api/intake/engagement/${engagementId}`,
        );
        if (!cancelled) setData(res.data as EngagementDetail);
      } catch (err: any) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : 'Could not load engagement.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [engagementId]);

  const handleDeploy = () => {
    setDeploying(true);
    // For the chatbot slug, the detail page is the integration surface.
    // Future marketplace solutions get their own /marketplace/:slug page.
    const slug = data?.stage1_solution?.slug || 'chatbot';
    navigate(`/marketplace/${slug}`);
  };

  return (
    <div style={shellStyles.page}>
      <div style={shellStyles.card}>
        <button
          type="button"
          onClick={() => navigate('/my-business#routing')}
          style={shellStyles.backLink}
        >
          <ChevronLeft size={14} strokeWidth={2} />
          Back to routing
        </button>

        <div style={{ ...shellStyles.iconCircle, background: 'rgba(24,25,29,0.10)', color: '#18191d' }}>
          <ShoppingBag size={26} strokeWidth={2} />
        </div>

        <div style={shellStyles.eyebrow}>Stage 2 · Direct deploy</div>

        {loading ? (
          <p style={shellStyles.bodyText}>
            <Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />
            Reading your engagement…
          </p>
        ) : error ? (
          <>
            <h1 style={shellStyles.title}>Couldn't load this engagement.</h1>
            <p style={shellStyles.bodyText}>{error}</p>
          </>
        ) : data ? (
          <>
            <h1 style={shellStyles.title}>
              Ready to deploy{' '}
              <em style={{ fontFamily: "var(--font-serif, 'IBM Plex Serif', serif)" }}>
                {data.stage1_solution?.name || 'your chatbot'}
              </em>.
            </h1>

            <p style={shellStyles.bodyText}>
              {data.stage1_solution?.tagline ||
                'Pre-built and trained on your website. Configure channels next.'}
            </p>

            {/* Channels */}
            {Array.isArray(data.stage1_solution?.channels) && data.stage1_solution.channels.length > 0 && (
              <div style={shellStyles.channelRow}>
                {data.stage1_solution.channels.map((channel: string) => (
                  <span key={channel} style={shellStyles.channelPill}>
                    {channel === 'website' && <Globe size={11} strokeWidth={2} />}
                    {channel === 'whatsapp' && <MessageSquare size={11} strokeWidth={2} />}
                    {channel === 'slack' && <MessageSquare size={11} strokeWidth={2} />}
                    {!['website', 'whatsapp', 'slack'].includes(channel) && <Sparkles size={11} strokeWidth={2} />}
                    {channel}
                  </span>
                ))}
              </div>
            )}

            {/* Cost reminder for tier transparency */}
            <p style={shellStyles.note}>
              Included on your {data.tier_at_decision || 'Free'} plan. No agent build burns
              from your monthly quota.
            </p>

            <div style={shellStyles.actions}>
              <button
                type="button"
                onClick={handleDeploy}
                disabled={deploying}
                style={shellStyles.primaryCta}
              >
                {deploying
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Sparkles size={14} strokeWidth={2.25} />}
                <span>Configure & deploy</span>
                <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}


// Shared full-screen outcome shell styles. Inlined here so each outcome
// page is self-contained. Pulled to a util later if we ship 3+ more.
const shellStyles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8f9fb',
    padding: 24,
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
  },
  card: {
    maxWidth: 560,
    width: '100%',
    background: '#fff',
    border: '1px solid #e2e5eb',
    borderRadius: 16,
    padding: '40px 40px 32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'transparent',
    border: 0,
    color: '#545454',
    fontSize: 12,
    cursor: 'pointer',
    padding: 0,
    marginBottom: 20,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  eyebrow: {
    fontFamily: "var(--font-sans, 'IBM Plex Sans', system-ui, sans-serif)",
    fontSize: 10.5,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#18191d',
    lineHeight: 1.3,
    marginBottom: 10,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: '-0.015em',
    color: '#000',
    margin: '0 0 12px',
    lineHeight: 1.25,
  },
  bodyText: {
    fontSize: 14.5,
    color: '#545454',
    lineHeight: 1.55,
    margin: '0 0 20px',
  },
  channelRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 20,
  },
  channelPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 9999,
    background: '#eef3ff',
    color: '#18191d',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  note: {
    fontSize: 12,
    color: '#545454',
    background: '#f7f9fc',
    border: '1px solid #e2e5eb',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 24,
    lineHeight: 1.55,
  },
  actions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  primaryCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '11px 18px',
    background: '#18191d',
    color: '#fff',
    border: 0,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
